import { IMsalContext, useMsal } from "@azure/msal-react";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ApiService } from "../../../Services/ApiService";
import { IUnitConfiguration } from "../../../Models/IUnitConfiguration";

// Material UI imports
import {
  Box,
  Typography,
  Card,
  CardContent,
  Container,
  useTheme,
  Chip,
  Paper,
  Breadcrumbs,
} from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import logger from '../../../Services/LoggingService';
import { createFlexStyles, createSpacingStyles, createBackgroundStyles, createTextStyles, createContentStyles } from '../../../theme/layoutStyles';

/**
 * ProductDesigns component - Material UI version
 * Displays available product designs based on URL query parameters
 *
 * @return {*} 
 */
export const ProductDesigns = () => {
    const ctx: IMsalContext = useMsal();
    const apiService = useMemo(() => new ApiService(ctx), [ctx]);
    const theme = useTheme();
    const flexStyles = createFlexStyles(theme);
    const spacingStyles = createSpacingStyles(theme);
    const backgroundStyles = createBackgroundStyles(theme);
    const textStyles = createTextStyles(theme);
    const contentStyles = createContentStyles(theme);
    const location = useLocation();
    const navigate = useNavigate();

    const [industry, setIndustry] = useState<string>('');
    const [subIndustry, setSubIndustry] = useState<string>('');
    const [application, setApplication] = useState<string>('');
    const [subApplication, setSubApplication] = useState<string>('');
    const [unitConfiguration, setUnitConfiguration] = useState<IUnitConfiguration[]>();
    const [productLine, setProductLine] = useState<string>('');
    const [alfaLavalPosition, setAlfaLavalPosition] = useState<string>('');

    // Consolidated styles object
    const styles = {
        // Main container styles
        mainContainer: {
            padding: theme.spacing(2),
            paddingTop: theme.spacing(3),
            minHeight: '100vh',
            backgroundColor: theme.palette.background.default
        },
        
        // Header styles
        headerContainer: {
            ...flexStyles.flexCenter,
            justifyContent: 'center', // Override for explicit center
            ...spacingStyles.marginBottom3
        },
        mainTitle: {
            color: theme.palette.text.primary,
            fontWeight: 600,
            ...textStyles.textCenter
        },
        
        // Breadcrumb styles
        breadcrumbContainer: {
            ...flexStyles.flexCenter,
            justifyContent: 'flex-start', // Override for left alignment
            ...spacingStyles.marginBottom4
        },
        breadcrumbPaper: {
            padding: theme.spacing(1.5, 2),
            ...backgroundStyles.lightGreyBackground,
            borderRadius: theme.shape.borderRadius,
            border: `1px solid ${theme.palette.divider}`
        },
        breadcrumbText: {
            color: theme.palette.text.secondary,
            fontSize: '0.875rem'
        },
        
        // Grid container styles
        designsContainer: {
            ...flexStyles.flexCenter,
            justifyContent: 'center' // Override for explicit center
        },
        designsGrid: {
            ...flexStyles.flexCenter,
            flexWrap: 'wrap',
            justifyContent: 'center', // Override for explicit center
            gap: theme.spacing(3),
            maxWidth: '1200px',
            width: '100%'
        },
        
        // Design card styles
        designCard: {
            width: 300,
            maxHeight: 201,
            cursor: 'pointer',
            transition: 'all 0.15s ease-in-out',
            border: `1px solid ${theme.palette.divider}`,
            '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: theme.shadows[16],
                borderColor: theme.palette.primary.main,
                '& .MuiCardContent-root': {
                    backgroundColor: theme.palette.primary.light + '08' // 8% opacity
                }
            }
        },
        cardContent: {
            padding: theme.spacing(2),
            ...flexStyles.flexColumn,
            ...contentStyles.centerContent,
            height: '100%',
            minHeight: 120,
            '&:last-child': {
                paddingBottom: theme.spacing(2)
            }
        },
        
        // Typography styles
        positionTitle: {
            fontWeight: 600,
            color: theme.palette.text.primary,
            marginBottom: theme.spacing(1)
        },
        productLineChip: {
            fontSize: '0.75rem',
            height: 24,
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.common.white,
            fontWeight: 500,
            '&:hover': {
                backgroundColor: theme.palette.primary.dark
            }
        },
        
        // Empty state styles
        emptyState: {
            ...contentStyles.emptyState
        },
        emptyStateTitle: {
            ...spacingStyles.marginBottom2,
            color: theme.palette.text.primary
        },
        
        // Utility styles
        flexCenter: {
            ...flexStyles.flexCenter,
            justifyContent: 'center' // Override for explicit center
        }
    };

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        setIndustry(queryParams.get('industry') as string);
        setSubIndustry(queryParams.get('subindustry') as string);
        setApplication(queryParams.get('application') as string);
        setSubApplication(queryParams.get('subapplication') as string);
        setProductLine(queryParams.get('productline') as string);
        setAlfaLavalPosition(queryParams.get('alfalavalposition') as string);

        async function getDesigns() {
            try {
                let ind = queryParams.get('industry') as string;
                let subInd = queryParams.get('subindustry') as string;
                let app = queryParams.get('application') as string;
                let subApp = queryParams.get('subapplication') as string;
                let alpos = queryParams.get('alfalavalposition') as string;

                const response = await apiService.getAsync(`settings/unitconfiguration/${ind}/${subInd}/${app}/${subApp}/${alpos}`);
                if (!response.ok) {
                    throw new Error(response.statusText);
                }

                const result = await response.json();
                setUnitConfiguration(result);
            } catch (error) {
                logger.error('Failed to fetch product designs', error as Error, {
                    industry: queryParams.get('industry'),
                    subIndustry: queryParams.get('subindustry'),
                    application: queryParams.get('application'),
                    subApplication: queryParams.get('subapplication'),
                    alfaLavalPosition: queryParams.get('alfalavalposition')
                }, {
                    component: 'ProductDesigns',
                    method: 'getDesigns'
                });
                setUnitConfiguration([]);
            }
        }

        getDesigns();
    }, [apiService, location.search]);

    const handleDesignClick = (item: IUnitConfiguration) => {
        navigate(`/productdesigns/productdesignform`, { state: item });
    };

    return (
        <Container maxWidth={false} sx={styles.mainContainer}>
            {/* Main title */}
            <Box sx={styles.headerContainer}>
                <Typography variant="h3" component="h1" sx={styles.mainTitle}>
                    Available designs
                </Typography>
            </Box>

            {/* Breadcrumb navigation */}
            <Box sx={styles.breadcrumbContainer}>
                <Paper sx={styles.breadcrumbPaper} elevation={0}>
                    <Breadcrumbs
                        separator={<NavigateNextIcon fontSize="small" />}
                        aria-label="breadcrumb"
                    >
                        <Typography sx={styles.breadcrumbText}>
                            {industry}
                        </Typography>
                        <Typography sx={styles.breadcrumbText}>
                            {subIndustry}
                        </Typography>
                        <Typography sx={styles.breadcrumbText}>
                            {application}
                        </Typography>
                        <Typography sx={styles.breadcrumbText} color="text.primary" fontWeight={500}>
                            {subApplication}
                        </Typography>
                    </Breadcrumbs>
                </Paper>
            </Box>

            {/* Designs grid */}
            <Box sx={styles.designsContainer}>
                {unitConfiguration && unitConfiguration.length > 0 ? (
                    <Box sx={styles.designsGrid}>
                        {unitConfiguration.map((item, index) => (
                            <Card
                                key={index}
                                sx={styles.designCard}
                                onClick={() => handleDesignClick(item)}
                                elevation={1}
                            >
                                <CardContent sx={styles.cardContent}>
                                    <Typography 
                                        variant="h6" 
                                        component="h2" 
                                        sx={styles.positionTitle}
                                    >
                                        {item.alfaLavalPosition}
                                    </Typography>
                                    
                                    <Chip
                                        label={item.productLine}
                                        size="small"
                                        sx={styles.productLineChip}
                                    />
                                </CardContent>
                            </Card>
                        ))}
                    </Box>
                ) : unitConfiguration && unitConfiguration.length === 0 ? (
                    <Box sx={styles.emptyState}>
                        <Typography variant="h5" sx={styles.emptyStateTitle}>
                            No designs available
                        </Typography>
                        <Typography variant="body1">
                            No product designs were found for the selected criteria.
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={styles.flexCenter}>
                        <Typography variant="body1" color="text.secondary">
                            Loading designs...
                        </Typography>
                    </Box>
                )}
            </Box>
        </Container>
    );
}; 